import { useTranslation } from 'react-i18next'

import { Alert, Button, ButtonWithSpinner, Drawer, Loading, Switch, Text } from '@reveni-io/reveni-ui'

import ExportPrices from './ExportPrices/ExportPrices'
import ItemInfo from './ItemInfo/ItemInfo'
import ProductLocations from './ProductLocations/ProductLocations'
import { formatCountryList, getCustomsCountryNames, getExportBlocks, isFormValid } from './helpers'
import * as Styles from './styles'

const EditItemInfoDrawer = ({
  item,
  product,
  onClose,
  show,
  loading,
  regions,
  locations,
  onChange,
  onConfirm,
  savingChanges,
  currency,
  applyInfoToAllVariants,
  onChangeApplyInfoToAllVariants,
  currencies,
  onPrevItem,
  onNextItem,
  customsRequirements,
  provider,
}) => {
  const { t, i18n } = useTranslation()

  if (!item) return null

  const exportBlocks = getExportBlocks({ customsRequirements, regions })
  const customsCountryNames = getCustomsCountryNames({
    customsRequirements,
    regions,
    europeanUnionName: t('Unión Europea'),
  })

  const handleOnChange = (field, value) => {
    onChange({ ...item, [field]: value })
  }

  const handleChangeProductLocations = value => handleOnChange('product_locations', value)

  const handleChangeExportPrices = value => handleOnChange('export_details', value)

  const handleChangeTaricCode = value => handleOnChange('taric_code', value || null)

  const handleChangeManufacturer = fields => onChange({ ...item, ...fields })

  const handleChangeApplyToAllVariants = e => onChangeApplyInfoToAllVariants(e.target.checked)

  return (
    <Drawer
      show={show}
      onClose={onClose}
      size="l"
      onPrevious={onPrevItem}
      onNext={onNextItem}
      options={{ showOnPrevious: true, showOnNext: true }}
    >
      <Loading show={loading} height="350" key={item.id}>
        <Styles.Container>
          <Styles.Header>
            <Text type="titleM">{t('Información del producto')}</Text>
          </Styles.Header>
          <ItemInfo
            item={item}
            product={product}
            regions={regions}
            onChange={handleOnChange}
            currency={currency}
            currencies={currencies}
            needsNsPid={customsRequirements?.needs_ns_pid}
            shipsWithCustoms={customsCountryNames.length > 0}
            customsCountries={formatCountryList(customsCountryNames, i18n.language)}
            provider={provider}
            applyInfoToAllVariants={applyInfoToAllVariants}
          />
          <Styles.FullWidthDivider />
          <ProductLocations
            productLocations={item?.product_locations}
            locations={locations}
            onChange={handleChangeProductLocations}
          />
          {exportBlocks.length > 0 && (
            <>
              <Styles.FullWidthDivider />
              <ExportPrices
                exportBlocks={exportBlocks}
                exportPrices={item.export_details}
                taricCode={item.taric_code}
                manufacturerName={item.manufacturer_name}
                manufacturerAddress={item.manufacturer_address}
                onChangeTaricCode={handleChangeTaricCode}
                onChangeManufacturer={handleChangeManufacturer}
                onChange={handleChangeExportPrices}
                currencies={currencies}
                hsCode={item.hs_code}
              />
            </>
          )}
          <Styles.Footer role="group" aria-label={t('Acciones')}>
            {product?.inventory_items?.length > 1 && (
              <Switch
                checked={applyInfoToAllVariants}
                onChange={handleChangeApplyToAllVariants}
                label={t('Cambiar datos en todas las variantes de este producto')}
              />
            )}
            {applyInfoToAllVariants && (
              <Alert type="warning">
                {t('Esta acción hará que se apliquen los mismos datos a todas las variantes de este producto')}
              </Alert>
            )}
            <Styles.FooterActions>
              <Button onClick={onClose} variant="outlined" fullWidth>
                {t('Cancelar')}
              </Button>
              <ButtonWithSpinner
                fullWidth
                onClick={onConfirm}
                defaultLabel={t('Guardar cambios')}
                loadingLabel={t('Guardando')}
                loading={savingChanges}
                disabled={!isFormValid(item, exportBlocks, applyInfoToAllVariants) || savingChanges}
              />
            </Styles.FooterActions>
          </Styles.Footer>
        </Styles.Container>
      </Loading>
    </Drawer>
  )
}

export default EditItemInfoDrawer
